/**
 * Global Auth Profile Notifications
 * Polls for auto-created/updated profiles and shows toast notifications
 * Works on ALL pages, not just AuthSmash tab
 *
 * Uses efficient single-request endpoint to avoid rate limiting
 */

import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';

// Persist which notification IDs have already been toasted so a page reload
// never re-shows an old toast. Kept small/pruned to avoid unbounded growth.
const SEEN_KEY = 'authprofile-notif-seen';
const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveSeen = (set) => {
  try {
    const arr = Array.from(set).slice(-500); // keep last 500
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch { /* ignore */ }
};

const GlobalAuthProfileNotifications = ({ webTargetId }) => {
  const shownNotifications = useRef(loadSeen());
  const isPolling = useRef(false);
  const pollInterval = useRef(15000); // Start with 15 seconds
  const rateLimitBackoff = useRef(1); // Multiplier for backoff

  useEffect(() => {
    if (!webTargetId || isPolling.current) return;

    isPolling.current = true;
    let intervalId = null;

    const pollForNotifications = async () => {
      try {
        // Use efficient single-request endpoint
        const response = await axios.get(`proxy-sessions/notifications/web-target/${webTargetId}`);
        const notifications = Array.isArray(response.data) ? response.data : [];

        // Reset backoff on successful request
        rateLimitBackoff.current = 1;
        pollInterval.current = 15000;

        // Show toast for new notifications
        notifications.forEach(notification => {
          const notifId = notification._id?.toString();
          if (notifId && !shownNotifications.current.has(notifId)) {
            shownNotifications.current.add(notifId);
            saveSeen(shownNotifications.current); // persist so a reload never re-shows it

            // Stay until the user closes it; never re-appear on reload
            const toastOpts = {
              position: "top-right",
              autoClose: false,
              hideProgressBar: true,
              closeOnClick: false,
              closeButton: true,
              pauseOnHover: true,
              draggable: true,
              toastId: notifId, // extra guard against duplicates in one session
            };
            if (notification.isUpdate) {
              toast.success(
                `🔄 Profile Updated: ${notification.profileName} (v${notification.previousVersion || 1} → v${(notification.previousVersion || 0) + 1})`,
                toastOpts
              );
            } else {
              toast.success(`✅ Profile Auto-Created: ${notification.profileName}`, toastOpts);
            }
          }
        });

      } catch (error) {
        // Handle auth errors
        if (error.response?.status === 401) {
          console.log('[Global Notifications] Auth error - stopping');
          isPolling.current = false;
          if (intervalId) clearInterval(intervalId);
          return;
        }

        // Handle rate limiting with exponential backoff
        if (error.response?.status === 429) {
          rateLimitBackoff.current = Math.min(rateLimitBackoff.current * 2, 8); // Max 8x backoff
          pollInterval.current = 15000 * rateLimitBackoff.current;
          console.log(`[Global Notifications] Rate limited - backing off to ${pollInterval.current / 1000}s`);

          // Restart interval with new timing
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = setInterval(pollForNotifications, pollInterval.current);
          }
          return;
        }

        // Log other errors silently
        console.error('[Global Notifications] Error polling:', error.message);
      }
    };

    // Initial poll after 2 seconds (give time for page to load)
    setTimeout(pollForNotifications, 2000);

    // Poll every 15 seconds (adjusts if rate limited)
    intervalId = setInterval(pollForNotifications, pollInterval.current);

    return () => {
      if (intervalId) clearInterval(intervalId);
      isPolling.current = false;
    };
  }, [webTargetId]);

  // This component doesn't render anything
  return null;
};

export default GlobalAuthProfileNotifications;
